/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t } from '@lingui/macro'
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import { Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { CreatePatientCarePartnerModal } from '@om1/aspen-api'
import { AspenPatientsService, PatientDTO, PatientProviderDTO, PatientUpsertDTO } from '@om1/aspen-api/codegen/client'
import { useCarePartnerByPatient } from '@om1/aspen-api/codegen/hooks/useCarePartnerByPatient'
import { useCioxRequests } from '@om1/aspen-api/codegen/hooks/useCioxRequests'
import { useContactAttempts } from '@om1/aspen-api/codegen/hooks/useContactAttempts'
import { usePatient } from '@om1/aspen-api/codegen/hooks/usePatient'
import { usePatientManagers } from '@om1/aspen-api/codegen/hooks/usePatientManagers'
import { usePatientOrders } from '@om1/aspen-api/codegen/hooks/usePatientOrders'
import { usePatientPatientTasks } from '@om1/aspen-api/codegen/hooks/usePatientPatientTasks'
import { usePatientProviders } from '@om1/aspen-api/codegen/hooks/usePatientProviders'
import { usePatientRequests } from '@om1/aspen-api/codegen/hooks/usePatientRequests'
import { formatDate } from '@om1/platform-components'
import { AlertCircle, Check, Clock, Edit, File, FileText, Phone, User, X } from 'lucide-react'
import { CopyableIdentifier } from '../components/CopyableIdentifier'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AdditionalPhoneData, AdditionalPhoneModal } from '../components/AdditionalPhoneModal'
import { CioxRequestData, CioxRequestInput } from '../components/CioxRequestInput'
import { CioxRequestTable } from '../components/CioxRequestTable'
import { DeleteConfirmModal } from '../components/DeleteConfirmModal'
import { GenericDetailTable, ValidColumn } from '../components/GenericDetailTable'
import GreenlightPatientWorkflowButton from '../components/GreenlightPatientWorkflowButton'
import LoadingComponent from '../components/LoadingComponent'
import { LogAttemptInput } from '../components/LogAttemptInput'
import { LogAttemptTable } from '../components/LogAttemptTable'
import { SubjectHistory } from '../components/SubjectHistory'
import ResendGreenlightNotificationButton from '../components/ResendGreenlightNotificationButton'
import SyncPatientChangesToSnowflakeButton from '../components/SyncPatientChangesToSnowflakeButton'
import GenericDetailPage from './GenericDetailPage'

interface PatientInfoSectionProps {
    title: string
    icon: React.ReactNode
    children: React.ReactNode
    className?: string
    titleAction?: React.ReactNode
}

type PatientWithCrucialSubjectId = {
    crucialSubjectId?: string | null
}

// Section component for grouping related information
const PatientInfoSection: React.FC<PatientInfoSectionProps> = ({ title, icon, children, className = '', titleAction }) => {
    return (
        <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm ${className}`}>
            <div className='flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50'>
                <div className='flex items-center gap-2'>
                    <div className='text-primary-main'>{icon}</div>
                    <h3 className='text-base font-semibold text-gray-800 m-0'>{title}</h3>
                </div>
                {titleAction}
            </div>
            <div className='p-4'>{children}</div>
        </div>
    )
}

// Simple status badge component using categorization
const StatusBadge = ({ status }: { status: string }) => {
    const category = categorizeStatus(status)
    const config = CATEGORY_CONFIG[category]
    const IconComponent = config.icon
    const displayStatus = formatStatusText(status)

    return (
        <div
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${config.bg} ${config.text} text-sm font-medium`}
            title={`${config.displayText}: ${status}`}
        >
            <IconComponent size={16} className='mr-1' />
            <span className='capitalize'>{displayStatus}</span>
        </div>
    )
}

// Status categories based on backend definitions (optimized with Sets for O(1) lookup)
const SUCCESS_STATUSES = new Set(['DATA_RETR', 'CLOSED'])
const PROBLEM_STATUSES = new Set([
    'CRED_SKIPPED',
    'CRED_FAILED',
    'FHIR_TOKEN_EXPIRED',
    'DATA_UNAV_2FA',
    'DATA_UNAV_NEEDS_USER_INTERACTION',
    'DOB_CHECK_FAILED',
    'CANCELLED'
])

// Utility function
const formatStatusText = (status: string) => status.replace(/_/g, ' ').toLowerCase()

const categorizeStatus = (status: string): 'success' | 'failed' | 'pending' => {
    if (SUCCESS_STATUSES.has(status)) return 'success'
    if (PROBLEM_STATUSES.has(status)) return 'failed'
    return 'pending' // Any other status is considered pending
}

// Simple category configuration
const CATEGORY_CONFIG = {
    success: { bg: 'bg-green-100', text: 'text-green-800', icon: Check, displayText: 'Success' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', icon: X, displayText: 'Failed' },
    pending: { bg: 'bg-blue-100', text: 'text-blue-800', icon: AlertCircle, displayText: 'Pending' }
} as const

// Simple categorized status badge component with working hover
const CategorizedStatusBadge = ({ status }: { status: string }) => {
    const category = categorizeStatus(status)
    const config = CATEGORY_CONFIG[category]
    const IconComponent = config.icon

    return (
        <Tooltip title={`${config.displayText}: ${status}`} arrow>
            <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${config.bg} ${config.text} text-sm font-medium cursor-help`}
                onClick={(e) => e.stopPropagation()}
            >
                <IconComponent size={16} className='mr-1' />
                <span>{config.displayText}</span>
            </span>
        </Tooltip>
    )
}

// Combined status display component (badge + raw status)
const StatusWithRaw = ({ status }: { status: string }) => (
    <div className='flex items-center gap-2'>
        <CategorizedStatusBadge status={status} />
    </div>
)

// Info field component for consistent display of information
const InfoField = ({ label, value }: { label: string; value?: string | React.ReactNode }) => {
    return (
        <div className='mb-3 last:mb-0'>
            <p className='text-xs text-gray-500 mb-0.5'>{label}</p>
            <p className='text-sm font-medium'>{value || 'N/A'}</p>
        </div>
    )
}

const PatientHeader = ({ patient }: { patient: PatientDTO }) => {
    return (
        <div className='flex-1 flex items-center'>
            <div className='flex items-center mr-4'>
                <div className='bg-primary-main text-white p-1.5 rounded-full flex-shrink-0 mr-3'>
                    <User size={20} />
                </div>
                <div className='min-w-0'>
                    <Typography variant='h6' className='font-semibold'>
                        {patient.firstName} {patient.lastName}
                    </Typography>
                    <CopyableIdentifier identifier={patient.friendlyId} />
                </div>
            </div>
            <div className='flex items-center ml-auto'>
                <div className='bg-blue-100 rounded-md px-4 py-1 flex items-center gap-8'>
                    <div>
                        <span className='font-medium'>Date of Birth:</span> <span>{formatDate(patient.dateOfBirth)}</span>
                    </div>
                    <div>
                        <span className='font-medium'>Phone Number:</span> <span>{patient.mobilePhone || 'N/A'}</span>
                    </div>
                    {patient.candidateFriendlyId && (
                        <div>
                            <span className='font-medium'>Candidate ID:</span>{' '}
                            <span className='font-mono text-sm'>{patient.candidateFriendlyId}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const PatientDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const { patient, fetchPatient } = usePatient(id)
    const { orders } = usePatientOrders(id)
    const { requests } = usePatientRequests(id)
    const { patientTasks, refetch: refetchPatientTasks, isNewTask } = usePatientPatientTasks(id)
    const { patientProviders } = usePatientProviders({ patientId: id })
    const { carePartner, loading: carePartnerLoading, refetch: refetchCarePartner } = useCarePartnerByPatient(id)

    // Most-recent order carrying a Greenlight id — the target for a notification
    // resend. useMemo so it only recomputes when orders change; shallow-copy so
    // the sort doesn't mutate the hook's array.
    const mostRecentGreenlightOrder = useMemo(
        () =>
            [...(orders?.data ?? [])]
                .sort((a, b) => new Date(b.createdDttm).getTime() - new Date(a.createdDttm).getTime())
                .find((order) => order.greenlightId),
        [orders]
    )

    // Fetch patient managers (including inactive for historical records) to pass to SubjectHistory
    // Memoize params to prevent infinite re-renders (usePatientManagers depends on params object reference)
    const patientManagersParams = useMemo(
        () => ({
            page: 1,
            limit: 1000, // Get all patient managers
            active: null, // Include inactive for historical records
            sort: 'name:asc'
        }),
        []
    )
    const { patientManagers } = usePatientManagers(patientManagersParams)

    const [showCarePartnerModal, setShowCarePartnerModal] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isCioxModalOpen, setIsCioxModalOpen] = useState(false)
    const [editingCioxRequest, setEditingCioxRequest] = useState<any>(null)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [requestToDelete, setRequestToDelete] = useState<string | null>(null)
    const [isAdditionalPhoneModalOpen, setIsAdditionalPhoneModalOpen] = useState(false)
    const [isUpdatingAdditionalPhones, setIsUpdatingAdditionalPhones] = useState(false)
    const [editingPhoneSlot, setEditingPhoneSlot] = useState<'phone1' | 'phone2' | undefined>(undefined)
    const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const {
        contactAttempts,
        isLoading: contactAttemptsLoading,
        error: contactAttemptsError,
        fetchContactAttempts,
        submitAttempt,
        updateAttempt
    } = useContactAttempts({
        entityType: 'patient',
        entityId: id || ''
    })

    const [editingAttempt, setEditingAttempt] = useState<any>(null)

    const {
        cioxRequests,
        isLoading: cioxRequestsLoading,
        error: cioxRequestsError,
        fetchCioxRequests,
        createCioxRequest,
        updateCioxRequest,
        deleteCioxRequest
    } = useCioxRequests({
        patientId: id || ''
    })

    useEffect(() => {
        if (id) {
            fetchContactAttempts()
            fetchCioxRequests()
        }
    }, [id, fetchContactAttempts, fetchCioxRequests])

    const handleOpenModal = () => {
        setIsModalOpen(true)
    }

    const handleOpenCioxModal = () => {
        setEditingCioxRequest(null)
        setIsCioxModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setEditingAttempt(null)
    }

    const handleCloseCioxModal = () => {
        setIsCioxModalOpen(false)
        setEditingCioxRequest(null)
    }

    const handleEditCioxRequest = (request: any) => {
        setEditingCioxRequest(request)
        setIsCioxModalOpen(true)
    }

    const handleDeleteCioxRequest = (requestId: string) => {
        setRequestToDelete(requestId)
        setDeleteConfirmOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (requestToDelete) {
            try {
                await deleteCioxRequest(requestToDelete)
                setDeleteConfirmOpen(false)
                setRequestToDelete(null)
            } catch (error) {
                console.error('Failed to delete ciox request:', error)
                // Error is handled by the hook
            }
        }
    }

    const handleCloseDeleteConfirm = () => {
        setDeleteConfirmOpen(false)
        setRequestToDelete(null)
    }

    const handleSubmitAttempt = async (data: any) => {
        await submitAttempt(data)
        handleCloseModal()
    }

    const handleEditAttempt = (attempt: any) => {
        setEditingAttempt(attempt)
        setIsModalOpen(true)
    }

    const handleUpdateAttempt = async (data: any) => {
        if (!editingAttempt) return
        const success = await updateAttempt({
            contactAttemptId: editingAttempt.id,
            contactType: data.contactType,
            outcome: data.outcome,
            notes: data.notes,
            nextContactTime: data.nextContactTime
        })
        if (success) {
            setEditingAttempt(null)
            handleCloseModal()
        }
    }

    const handleSubmitCioxRequest = async (data: CioxRequestData) => {
        try {
            if (editingCioxRequest) {
                // Update existing request
                await updateCioxRequest(editingCioxRequest.id, { providerName: data.providerName })
            } else {
                // Create new request
                await createCioxRequest(data)
            }
            handleCloseCioxModal()
        } catch (error) {
            console.error('Failed to save ciox request:', error)
            // Error is handled by the hook and displayed in the modal
        }
    }

    const handleUpdateAdditionalPhones = async (data: AdditionalPhoneData) => {
        if (!patient) return

        setIsUpdatingAdditionalPhones(true)
        try {
            const upsertData: PatientUpsertDTO = {
                externalId: patient.externalId,
                id: patient.id
            }

            // Update the appropriate slot
            if (data.slot === 'phone1') {
                upsertData.additionalPhone1 = data.phone
                upsertData.additionalPhone1Label = data.label
            } else {
                upsertData.additionalPhone2 = data.phone
                upsertData.additionalPhone2Label = data.label
            }

            await AspenPatientsService.upsertPatientsPost({ requestBody: upsertData })
            await fetchPatient()
            setIsAdditionalPhoneModalOpen(false)
        } catch (error) {
            console.error('Error updating additional phone numbers:', error)
            throw error
        } finally {
            setIsUpdatingAdditionalPhones(false)
        }
    }

    const handleCioxStatusChange = async (requestId: string, newStatus: string) => {
        try {
            await updateCioxRequest(requestId, { status: newStatus })
        } catch (error) {
            console.error('Failed to update ciox request status:', error)
            // Error is handled by the hook
        }
    }

    const debouncedRefetchTasks = useCallback(() => {
        if (refetchTimeoutRef.current) {
            clearTimeout(refetchTimeoutRef.current)
        }
        refetchTimeoutRef.current = setTimeout(() => {
            refetchPatientTasks()
        }, 1000) // Debounce for 1 second
    }, [refetchPatientTasks])

    const handleCarePartnerSuccess = (carePartnerId: string) => {
        refetchCarePartner()
    }

    // Cleanup timeout on unmount
    React.useEffect(() => {
        return () => {
            if (refetchTimeoutRef.current) {
                clearTimeout(refetchTimeoutRef.current)
            }
        }
    }, [])

    if (!patient) {
        return (
            <div className='fixed inset-0 flex flex-col justify-center items-center'>
                <LoadingComponent />
            </div>
        )
    }

    return (
        <>
            <GenericDetailPage
                backButtonText={t`To Patients`}
                title={<PatientHeader patient={patient} />}
                data={patient}
                backRoute='/patients'
                columns={[]}
                actionButtons={[
                    <div key='patient-actions' className='flex justify-end gap-3 flex-wrap'>
                        <GreenlightPatientWorkflowButton
                            patientId={patient.id}
                            hasProviders={true}
                            enrolled={patient.greenlightEnrolled}
                            onSuccess={debouncedRefetchTasks}
                        />
                        <SyncPatientChangesToSnowflakeButton
                            patientId={patient.id}
                            hasGreenlightId={patient.greenlightId !== null && patient.greenlightId !== undefined}
                            onSuccess={debouncedRefetchTasks}
                        />
                        <ResendGreenlightNotificationButton
                            patientId={patient.id}
                            mostRecentOrderId={mostRecentGreenlightOrder?.id}
                            mostRecentOrderGreenlightId={mostRecentGreenlightOrder?.greenlightId}
                            hasOrders={(orders?.data?.length ?? 0) > 0}
                        />
                    </div>
                ]}
                additionalComponents={[
                    <div key='content-layout' className='grid grid-cols-1 md:grid-cols-12 gap-4 pb-16'>
                        <div className='md:col-span-8 space-y-4'>
                            <PatientInfoSection
                                title='Contact Information'
                                icon={<User size={18} />}
                                titleAction={
                                    /* Only show "Add Additional Phone" button if there's at least one slot available */
                                    (!patient.additionalPhone1 || !patient.additionalPhone2) && (
                                        <Button
                                            color='primary'
                                            onClick={() => {
                                                setEditingPhoneSlot(undefined)
                                                setIsAdditionalPhoneModalOpen(true)
                                            }}
                                            size='small'
                                            variant='outlined'
                                            className='h-8'
                                            startIcon={<Phone size={16} />}
                                        >
                                            {t`Add Additional Phone`}
                                        </Button>
                                    )
                                }
                            >
                                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                    <InfoField label={t`Email`} value={patient.email} />
                                    <InfoField label={t`Mobile Phone`} value={patient.mobilePhone} />
                                    {(patient.additionalPhone1 || patient.additionalPhone2) && (
                                        <>
                                            {patient.additionalPhone1 && (
                                                <div className='mb-3 last:mb-0'>
                                                    <div className='flex items-center gap-2 mb-0.5'>
                                                        <p className='text-xs text-gray-500'>
                                                            {patient.additionalPhone1Label
                                                                ? `${patient.additionalPhone1Label} Phone`
                                                                : t`Additional Phone 1`}
                                                        </p>
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditingPhoneSlot('phone1')
                                                                setIsAdditionalPhoneModalOpen(true)
                                                            }}
                                                            size='small'
                                                            className='h-6 w-6 p-0'
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <Edit size={16} />
                                                        </IconButton>
                                                    </div>
                                                    <p className='text-sm font-medium'>{patient.additionalPhone1 || 'N/A'}</p>
                                                </div>
                                            )}
                                            {patient.additionalPhone2 && (
                                                <div className='mb-3 last:mb-0'>
                                                    <div className='flex items-center gap-2 mb-0.5'>
                                                        <p className='text-xs text-gray-500'>
                                                            {patient.additionalPhone2Label
                                                                ? `${patient.additionalPhone2Label} Phone`
                                                                : t`Additional Phone 2`}
                                                        </p>
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditingPhoneSlot('phone2')
                                                                setIsAdditionalPhoneModalOpen(true)
                                                            }}
                                                            size='small'
                                                            className='h-6 w-6 p-0'
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <Edit size={16} />
                                                        </IconButton>
                                                    </div>
                                                    <p className='text-sm font-medium'>{patient.additionalPhone2 || 'N/A'}</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <InfoField label={t`Date of Birth`} value={formatDate(patient.dateOfBirth)} />
                                    <InfoField label={t`Preferred Communication`} value={patient.patientPreferredCommunication} />
                                    <InfoField label={t`Site Name`} value={patient.siteName} />
                                    <InfoField label={t`Site ID`} value={patient.siteId} />
                                </div>
                            </PatientInfoSection>

                            <PatientInfoSection title={t`Providers`} icon={<User className='h-5 w-5' />}>
                                {(() => {
                                    const providerColumns = [
                                        'provider.name',
                                        'provider.zipCode',
                                        'createdDttm',
                                        'source'
                                    ] as ValidColumn<PatientProviderDTO>[]
                                    return (
                                        <GenericDetailTable
                                            className='max-h-[300px] overflow-auto'
                                            data={patientProviders}
                                            columns={providerColumns}
                                        />
                                    )
                                })()}
                            </PatientInfoSection>

                            <PatientInfoSection
                                title='Contact Attempts'
                                icon={<PhoneIcon className='text-primary-main h-5 w-5' />}
                                titleAction={
                                    <Button
                                        color='primary'
                                        onClick={handleOpenModal}
                                        startIcon={<span>{t`+`}</span>}
                                        className='bg-primary-main text-white hover:bg-gray-200 rounded-md h-8'
                                        size='small'
                                    >
                                        {t`Add New Attempt`}
                                    </Button>
                                }
                            >
                                {contactAttemptsLoading ? (
                                    <LoadingComponent />
                                ) : (
                                    <div className='h-[200px] overflow-visible'>
                                        <LogAttemptTable attempts={contactAttempts} onEdit={handleEditAttempt} />
                                    </div>
                                )}
                            </PatientInfoSection>

                            <SubjectHistory subjectType='patient' subjectId={id || ''} onUpdate={fetchPatient} patientManagers={patientManagers} />
                        </div>

                        <div className='md:col-span-4 space-y-4'>
                            <PatientInfoSection title='Additional Details' icon={<FileText size={18} />}>
                                <div className='space-y-3'>
                                    <div className='mb-3'>
                                        <p className='text-xs text-gray-500 mb-0.5'>{t`Greenlight ID`}</p>
                                        <div className='flex items-center'>
                                            <Tooltip
                                                title={
                                                    patient.greenlightId
                                                        ? patient.greenlightId
                                                        : patient.greenlightEnrolled
                                                        ? t`Enrolled in Greenlight`
                                                        : t`No Greenlight ID`
                                                }
                                            >
                                                <span className='flex items-center mr-2'>
                                                    {patient.greenlightId || patient.greenlightEnrolled ? (
                                                        <MedicalInformationIcon className='text-green-800' />
                                                    ) : (
                                                        <PersonIcon className='text-gray-400' />
                                                    )}
                                                </span>
                                            </Tooltip>
                                            <p className='text-sm font-medium'>
                                                {patient.greenlightId || (patient.greenlightEnrolled ? t`Enrolled` : 'Not connected')}
                                            </p>
                                        </div>
                                    </div>
                                    <InfoField
                                        label={t`TrialKit Subject ID`}
                                        value={(patient as PatientWithCrucialSubjectId).crucialSubjectId || 'N/A'}
                                    />
                                    <InfoField label={t`Zip Code`} value={patient.zipCode} />
                                    <InfoField label={t`Created By`} value={patient.createdByEmail} />
                                    <InfoField label={t`Created At`} value={formatDate(patient.createdDttm)} />
                                    <InfoField label={t`Updated At`} value={formatDate(patient.updatedDttm)} />
                                </div>
                            </PatientInfoSection>

                            <PatientInfoSection
                                title={t`Care Partner`}
                                icon={<User className='h-5 w-5' />}
                                titleAction={
                                    carePartner && (
                                        <Button
                                            onClick={() => setShowCarePartnerModal(true)}
                                            variant='outlined'
                                            size='small'
                                            startIcon={<span>✏️</span>}
                                            className='bg-white border border-gray-200 text-primary-main hover:bg-gray-50 rounded-md h-8'
                                        >
                                            {t`Edit Care Partner`}
                                        </Button>
                                    )
                                }
                            >
                                {carePartnerLoading ? (
                                    <div className='flex items-center justify-center py-4'>
                                        <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary-main'></div>
                                    </div>
                                ) : carePartner ? (
                                    <div className='space-y-3'>
                                        <InfoField label={t`Care Partner Name`} value={`${carePartner.firstName} ${carePartner.lastName}`} />
                                        <InfoField label={t`Phone Number`} value={carePartner.phoneNumber} />
                                        {carePartner.email && <InfoField label={t`Email Address`} value={carePartner.email} />}
                                        <InfoField label={t`Created`} value={formatDate(carePartner.createdDttm)} />
                                    </div>
                                ) : (
                                    <div className='text-center py-4'>
                                        <p className='text-sm text-gray-500 mb-2'>No care partner assigned</p>
                                        <p className='text-xs text-gray-400'>
                                            Care partners provide support and can be contacted regarding this patient
                                        </p>
                                    </div>
                                )}
                            </PatientInfoSection>
                        </div>

                        <div className='md:col-span-12'>
                            <PatientInfoSection title='Orders' icon={<Clock size={18} />}>
                                <GenericDetailTable
                                    data={orders}
                                    columns={['greenlightId', 'createdByEmail', 'createdDttm']}
                                    additionalColumns={[
                                        {
                                            label: 'External Order Id',
                                            value: (item) => <CopyableIdentifier identifier={item.id} />
                                        }
                                    ]}
                                    className='max-h-[300px] overflow-y-auto'
                                    stickyHeader={true}
                                />
                            </PatientInfoSection>
                        </div>

                        <div className='md:col-span-12'>
                            <PatientInfoSection title='Greenlight Requests' icon={<FileText size={18} />}>
                                <GenericDetailTable
                                    data={requests}
                                    columns={['greenlightId', 'provider.name', 'changesNextStartingTimestamp', 'createdByEmail', 'createdDttm']}
                                    additionalColumns={[
                                        {
                                            label: 'Status',
                                            value: (item) => <StatusWithRaw status={item.status} />
                                        }
                                    ]}
                                    className='max-h-[300px] overflow-y-auto'
                                    stickyHeader={true}
                                />
                            </PatientInfoSection>
                        </div>

                        <div className='md:col-span-12'>
                            <PatientInfoSection
                                title='Ciox Requests'
                                icon={<File className='text-primary-main h-5 w-5' />}
                                titleAction={
                                    <Button
                                        color='primary'
                                        onClick={handleOpenCioxModal}
                                        startIcon={<span>{t`+`}</span>}
                                        className='bg-primary-main text-white hover:bg-gray-200 rounded-md h-8'
                                        size='small'
                                    >
                                        {t`Add Ciox Request`}
                                    </Button>
                                }
                            >
                                {cioxRequestsLoading ? (
                                    <LoadingComponent />
                                ) : (
                                    <div className='h-[200px] overflow-visible'>
                                        <CioxRequestTable
                                            requests={cioxRequests as any}
                                            onStatusChange={handleCioxStatusChange}
                                            onEdit={handleEditCioxRequest}
                                            onDelete={handleDeleteCioxRequest}
                                        />
                                    </div>
                                )}
                            </PatientInfoSection>
                        </div>

                        <div className='md:col-span-12'>
                            <PatientInfoSection title='Tasks' icon={<Clock size={18} />} className='mb-4'>
                                <GenericDetailTable
                                    data={patientTasks}
                                    type='task'
                                    columns={['taskId']}
                                    stickyHeader={true}
                                    idResolver={(item) => item.taskId}
                                    additionalColumns={[
                                        {
                                            label: 'Name',
                                            value: (item) => (
                                                <div className='flex items-center gap-2'>
                                                    <span>{item.task.name?.replace('om1.modules.aspen.worker.tasks.', '')}</span>
                                                    {isNewTask(item.taskId) && (
                                                        <span className='px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded'>
                                                            New
                                                        </span>
                                                    )}
                                                </div>
                                            )
                                        },
                                        {
                                            label: 'Status',
                                            value: (item) => <StatusBadge status={item.task.status} />
                                        },
                                        {
                                            label: 'Created',
                                            value: (item) => <span>{formatDate(item.taskCreatedDttm)}</span>
                                        }
                                    ]}
                                    className='max-h-[300px] overflow-y-auto'
                                />
                            </PatientInfoSection>
                        </div>
                    </div>
                ]}
            />

            {/* Modal for adding/editing contact attempt */}
            <Dialog
                open={isModalOpen}
                onClose={handleCloseModal}
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        width: '500px',
                        maxHeight: '80vh'
                    }
                }}
            >
                <DialogContent sx={{ p: 3 }}>
                    <div className='flex justify-center items-center min-h-[60vh]'>
                        <div className='w-full'>
                            <LogAttemptInput
                                entityName={`${patient.firstName} ${patient.lastName}`}
                                onSubmit={editingAttempt ? handleUpdateAttempt : handleSubmitAttempt}
                                onCancel={handleCloseModal}
                                entityType='patient'
                                entityId={patient.id}
                                contactAttempts={contactAttempts}
                                isLoading={contactAttemptsLoading}
                                error={contactAttemptsError}
                                initialValues={
                                    editingAttempt
                                        ? {
                                              contactType: editingAttempt.contactType,
                                              outcome: editingAttempt.attemptOutcome,
                                              notes: editingAttempt.notes,
                                              nextContactTime: editingAttempt.suggestedNextContactTime
                                                  ? new Date(editingAttempt.suggestedNextContactTime)
                                                  : null
                                          }
                                        : undefined
                                }
                            />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal for adding new ciox request */}
            <Dialog
                open={isCioxModalOpen}
                onClose={handleCloseCioxModal}
                maxWidth='sm'
                fullWidth
                PaperProps={{
                    sx: {
                        width: '400px',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogContent sx={{ p: 4 }}>
                    <CioxRequestInput
                        onSubmit={handleSubmitCioxRequest}
                        onCancel={handleCloseCioxModal}
                        isLoading={cioxRequestsLoading}
                        error={cioxRequestsError}
                        initialValue={editingCioxRequest?.providerName || ''}
                        isEditMode={!!editingCioxRequest}
                    />
                </DialogContent>
            </Dialog>

            <CreatePatientCarePartnerModal
                open={showCarePartnerModal}
                onClose={() => setShowCarePartnerModal(false)}
                onSuccess={handleCarePartnerSuccess}
                patientId={id}
                mode='edit'
                carePartner={carePartner}
            />

            <AdditionalPhoneModal
                open={isAdditionalPhoneModalOpen}
                onClose={() => {
                    setIsAdditionalPhoneModalOpen(false)
                    setEditingPhoneSlot(undefined)
                }}
                onSave={handleUpdateAdditionalPhones}
                initialData={{
                    phone1: patient?.additionalPhone1 || null,
                    phone1Label: patient?.additionalPhone1Label || null,
                    phone2: patient?.additionalPhone2 || null,
                    phone2Label: patient?.additionalPhone2Label || null
                }}
                loading={isUpdatingAdditionalPhones}
                entityType='patient'
                editingSlot={editingPhoneSlot}
            />

            <DeleteConfirmModal
                open={deleteConfirmOpen}
                onClose={handleCloseDeleteConfirm}
                onConfirm={handleConfirmDelete}
                title={t`Delete Ciox Request`}
                message={
                    requestToDelete
                        ? t`Are you sure you want to delete the ciox request for ${
                              cioxRequests.find((r) => r.id === requestToDelete)?.providerName || ''
                          }? This action cannot be undone.`
                        : t`Are you sure you want to delete this ciox request? This action cannot be undone.`
                }
                isLoading={cioxRequestsLoading}
            />
        </>
    )
}

export default PatientDetailPage
